import {analyze, compileKeyword, matchesKeyword, scoreKeywords} from "./nlp.utils";
import {
  COMPANIES,
  DEFAULT_SUGGESTIONS,
  EDUCATION,
  PROFILE,
  PROJECTS,
  SKILL_ALIASES,
  SKILL_GROUPS,
} from "./knowledge-base.constants";

// Minimum keyword score for an intent to be considered a match.
const MATCH_THRESHOLD = 0.5;

const bullets = (items) => items.map((item) => `• ${item}`).join("\n");

const yearsOfExperience = () => {
  const now = new Date();
  const start = PROFILE.careerStart;
  const months = (now.getFullYear() - start.getFullYear()) * 12 + now.getMonth() - start.getMonth();
  return Math.floor(months / 12);
};

const currentCompany = () => COMPANIES[0];

// " (employed through Unify Technologies)" when the work is done via an employer.
const employerNote = (company) => (company.employer ? ` (employed through ${company.employer})` : "");

/* ------------------------------------------------------------------
   Intents: general questions answered from keywords alone
   ------------------------------------------------------------------ */

const INTENTS = [
  {
    id: "greeting",
    keywords: [["hi", 0.6], ["hii", 0.6], ["hello", 0.6], ["hey", 0.6], ["namaste", 0.6], ["hola", 0.6], ["yo", 0.6], ["good morning", 0.6], ["good evening", 0.6], ["good afternoon", 0.6]],
    answer: () => "Hey there! 👋 I'm Suraj's portfolio assistant. Ask me anything about his career, skills, projects or education.",
  },
  {
    id: "thanks",
    keywords: [["thanks", 0.6], ["thank", 0.6], ["thx", 0.6], ["great", 0.6], ["awesome", 0.6], ["cool", 0.6], ["nice", 0.6], ["helpful", 0.6]],
    answer: () => "Happy to help! Anything else you'd like to know about Suraj?",
  },
  {
    id: "bye",
    keywords: [["bye", 0.6], ["goodbye", 0.6], ["see you", 0.6], ["later", 0.6]],
    answer: () => `Bye! Feel free to reach Suraj at ${PROFILE.email} anytime.`,
  },
  {
    id: "bot",
    keywords: ["bot", "chatbot", "robot", "assistant", "human", "real person", "ai bot", "who built you", "who made you"],
    answer: () =>
      "I'm a small rule-based assistant built into Suraj's portfolio. I match your question against what I know about him — no LLM involved — so short, direct questions work best!",
  },
  {
    id: "intro",
    keywords: [["who", 0.8], "introduce", "introduction", "yourself", "summary", "overview", "profile", "background", "describe", "bio", "person"],
    answer: () =>
      `${PROFILE.name} is a ${PROFILE.title} based in ${PROFILE.location}, specialising in backend development and system design.\n\n` +
      `He has ${yearsOfExperience()}+ years of experience building robust systems with Scala, Java, Play, Spring Boot, Spark and AWS, and has recently moved into AI/ML. ` +
      `He currently works directly with Apple's teams on ${currentCompany().name}${employerNote(currentCompany())}, where he's also building an agentic AI solution with LangChain.\n\n` +
      "Fun fact: he started his career as a Mechanical Engineer before switching to software!",
    suggestions: ["Where does he work now?", "Why did he switch to IT?", "What are his skills?"],
  },
  {
    id: "name",
    keywords: ["name", "full name", "called"],
    answer: () => `His name is ${PROFILE.name}.`,
  },
  {
    id: "current_job",
    keywords: [["current", 1.5], ["currently", 1.5], ["now", 1.5], ["present", 1.5], ["today", 1], "working", "employer", "employ", "job", ["company", 0.8], ["work", 0.8], ["role", 0.8], "designation", "position", ["client", 1.5]],
    answer: () => {
      const company = currentCompany();
      return (
        `Suraj is currently a ${company.role} on ${company.name} (${company.period})${company.employer ? `, employed through ${company.employer}` : ""}.\n\n${company.summary}\n\n` +
        `Tech he uses there: ${company.stack.join(", ")}.`
      );
    },
    context: () => ({lastCompany: currentCompany().id}),
    suggestions: ["What does he do there?", "Where did he work before?", "Total experience?"],
  },
  {
    id: "experience",
    keywords: ["experience", "year", "how long", "career", "journey", ["=companies", 2], ["=worked", 1], "previous", "past", "history", "timeline", "before", "prior", "background work", "fresher", "senior"],
    answer: () =>
      `Suraj has ${yearsOfExperience()}+ years of professional software experience:\n` +
      bullets(COMPANIES.filter((c) => c.id !== "masai_school").map((c) => `${c.name} — ${c.role} (${c.period}${c.employer ? `, via ${c.employer}` : ""})`)) +
      "\n\nBefore that, he trained at Masai School and worked as a Mechanical Engineer.",
    suggestions: ["Tell me about Apple Maps", "Tell me about Rawcubes", "Tell me about Zomentum"],
  },
  {
    id: "skills",
    keywords: ["skill", "tech", "technology", "technologies", "stack", "tech stack", "language", "programming", "programming language", "framework", "tool", "expertise", "proficient", "database", "cloud", "frontend", "backend", "big data", "devops", "good at", "strength", "specialize", "specialise", "expert"],
    answer: () =>
      "Suraj specialises in backend development and system design. His toolkit:\n" +
      bullets(SKILL_GROUPS.map(([group, skills]) => `${group}: ${skills.join(", ")}`)),
    suggestions: ["Does he know Spark?", "Does he know React?", "What projects has he built?"],
  },
  {
    id: "ml",
    keywords: [["machine learning", 3], ["ml", 2], ["ai", 2], "artificial intelligence", "llm", "genai", "gen ai", "data science", "deep learning", "model", ["agentic", 3], ["agent", 2], ["ai agent", 3]],
    answer: () =>
      "Yes — AI/ML is a big part of Suraj's current work on Apple Maps:\n" +
      bullets([
        "Building an agentic AI solution using LangChain, with LLM-powered agents that automate multi-step workflows.",
        "Developing AI/ML models for intelligent address placement.",
      ]) +
      "\n\nHis AI/ML toolkit includes LangChain, Langfuse and TensorFlow.",
    suggestions: ["Where does he work now?", "What are his skills?"],
  },
  {
    id: "education",
    keywords: [["education", 2], ["school", 2], ["schooling", 2], ["college", 2], ["university", 2], ["degree", 2], ["study", 2], ["graduat", 2], ["qualification", 2], ["academic", 2], ["btech", 2], ["b tech", 2], ["engineering", 1.5], ["course", 1], ["bootcamp", 1.5], ["learn", 1], ["learned", 1], ["certification", 1.5], ["educational", 2]],
    answer: () =>
      "Suraj's education:\n" +
      bullets(EDUCATION.map((e) => `${e.name} — ${e.detail} (${e.period})`)) +
      "\n\nHe comes from a Mechanical Engineering background and moved into software by completing Masai School's intensive full-stack program.",
    suggestions: ["Why did he switch to IT?", "What did he do at Masai?", "Where does he work now?"],
  },
  {
    id: "early_career",
    keywords: [["mechanical", 2], ["switch", 2], ["transition", 2], ["change career", 3], ["career change", 3], ["move to it", 3], ["why it", 3], ["covid", 2], ["pandemic", 2], ["corrugation", 2], ["wire drawing", 3], ["early", 1.5], ["early life", 3], ["start", 1], ["first job", 3], ["non it", 3]],
    answer: () =>
      "Suraj began his career as a Mechanical Engineer — working in production at a corrugation plant (running galvanizing & annealing lines) and in quality at a wire-drawing machine manufacturer.\n\n" +
      "When COVID-19 brought layoffs, he decided to pivot to IT, joined Masai School in July 2021, and has never looked back since. 🚀",
    suggestions: ["Tell me about Masai School", "Total experience?", "What are his skills?"],
  },
  {
    id: "projects",
    keywords: [["project", 2], "built", "build", "portfolio", "side project", "github repo", "made", "application", "app", "clone", "personal project", "work sample"],
    answer: () =>
      "Some projects Suraj has built:\n" +
      bullets(PROJECTS.map((p) => `${p.name} — ${p.stack.slice(0, 3).join(", ")}`)) +
      `\n\nMore on GitHub: ${PROFILE.github}`,
    suggestions: ["Tell me about Medikal", "Tell me about Contact Manager", "Does he know Spring Boot?"],
  },
  {
    id: "location",
    keywords: [["where", 0.5], "live", "location", "located", "based", "city", "hometown", "native", "indore", "india", "country", "relocate", "relocation", "remote"],
    answer: () =>
      `Suraj is based in ${PROFILE.location}. For relocation or remote opportunities, it's best to reach out to him directly at ${PROFILE.email}.`,
  },
  {
    id: "contact",
    keywords: [["contact", 2], ["email", 2], ["mail", 2], ["phone", 2], ["number", 1], ["call", 1], ["reach", 2], ["connect", 1.5], ["message", 1], ["mobile", 2], ["whatsapp", 2], ["get in touch", 3]],
    answer: () =>
      `You can reach Suraj at:\n• Email: ${PROFILE.email}\n• Phone: ${PROFILE.phone}\n• LinkedIn: ${PROFILE.linkedin}\n\nOr use the contact form at the bottom of this page!`,
  },
  {
    id: "social",
    keywords: [["linkedin", 2], ["github", 2], ["social", 2], ["instagram", 2], ["twitter", 2], ["profile link", 2], ["handle", 1]],
    answer: () => `Find Suraj online:\n• LinkedIn: ${PROFILE.linkedin}\n• GitHub: ${PROFILE.github}`,
  },
  {
    id: "resume",
    keywords: [["resume", 3], ["cv", 3], ["biodata", 3], ["download", 1]],
    answer: () => `Here's Suraj's resume: ${PROFILE.resume}\n\nYou can also use the "View Resume" button in the About section.`,
  },
  {
    id: "hiring",
    keywords: [["hire", 3], ["hiring", 3], ["available", 2], ["availability", 2], ["opportunit", 2], ["open to", 2], ["freelance", 2], ["recruit", 2], ["job offer", 3], ["notice period", 3], ["looking for job", 3], ["interview", 2]],
    answer: () =>
      `Interested in working with Suraj? Drop him a line at ${PROFILE.email} or connect on LinkedIn (${PROFILE.linkedin}) — he'll be happy to chat about opportunities.`,
    suggestions: ["Can I see his resume?", "What are his skills?"],
  },
  {
    id: "hobbies",
    keywords: [["hobby", 3], ["hobbies", 3], ["free time", 3], ["spare time", 3], ["interest", 2], ["chess", 2], ["reading", 2], ["read", 1.5], ["fun", 1.5], ["enjoy", 1.5], ["pastime", 3], ["leisure", 3], ["sport", 2], ["play", 1], ["game", 1.5], ["outside work", 3]],
    answer: () => `When he's not coding for work, Suraj enjoys ${PROFILE.hobbies.toLowerCase()}. ♟️📚`,
  },
  {
    id: "spoken_languages",
    keywords: [["speak", 3], ["spoken", 3], ["mother tongue", 3], ["hindi", 3], ["english", 3], ["brij", 3], ["communication", 2], ["languages speak", 3]],
    answer: () => `Suraj speaks ${PROFILE.spokenLanguages}.`,
  },
  {
    id: "blog",
    keywords: [["blog", 3], ["article", 2], ["post", 1.5], ["write", 1.5], ["writing", 1.5]],
    answer: () => "Suraj writes on his blog right here on the portfolio — click \"Blog\" in the navigation bar to read his posts.",
  },
  {
    id: "personal",
    keywords: [["age", 2], ["old", 1.5], ["birthday", 2], ["born", 2], ["married", 2], ["salary", 2], ["ctc", 2], ["package", 1.5], ["girlfriend", 2], ["wife", 2], ["religion", 2]],
    answer: () =>
      `That's a bit personal for me to share. 🙂 If it's relevant (e.g. compensation for a role), please reach out to Suraj directly at ${PROFILE.email}.`,
  },
].map((intent) => ({...intent, compiled: intent.keywords.map(compileKeyword)}));

// Intents where a mentioned technology should take over the answer
// ("what projects use Spark?" -> talk about Spark).
const TECH_INTENTS = new Set(["skills", "experience", "projects", "current_job", "ml", "intro", "bot"]);
const SMALL_TALK = new Set(["greeting", "thanks", "bye"]);

/* ------------------------------------------------------------------
   Entities: companies, projects and skills mentioned by name
   ------------------------------------------------------------------ */

const compileEntities = (items) =>
  items.map((item) => ({...item, compiled: item.aliases.map(compileKeyword)}));

const COMPANY_ENTITIES = compileEntities(COMPANIES);
const PROJECT_ENTITIES = compileEntities(PROJECTS);
const SKILL_ENTITIES = compileEntities(
  Object.entries(SKILL_ALIASES).map(([name, aliases]) => ({id: name, name, aliases}))
);

const detectEntities = (entities, analysis) =>
  entities.filter((entity) => entity.compiled.some((k) => matchesKeyword(k, analysis)));

// Which part of a company/project the question is about.
const ASPECTS = [
  ["stack", ["stack", "tech", "technology", "technologies", "tool", "language", "framework", "use", "used", "using"]],
  ["duration", ["when", "how long", "duration", "period", "join", "joined", "leave", "left", "since", "till", "until", "date", "tenure"]],
  ["role", ["role", "position", "designation", "title"]],
  ["work", ["work", "do", "did", "responsibility", "responsibilities", "task", "highlight", "achievement", "contribution", "built", "more", "detail", "details", "elaborate"]],
].map(([id, keywords]) => [id, keywords.map(compileKeyword)]);

// Aspect detection runs on every word (not just content words) because
// "do", "did" and "about" are stopwords but meaningful here.
const detectAspect = (analysis) => {
  const allWords = {...analysis, content: analysis.words};
  const found = ASPECTS.find(([, keywords]) => keywords.some((k) => matchesKeyword(k, allWords)));
  return found ? found[0] : null;
};

/* ------------------------------------------------------------------
   Entity answers
   ------------------------------------------------------------------ */

const companyAnswer = (company, aspect) => {
  const [from, to] = company.period.split(" - ");
  const isCurrent = to === "Present";
  const label = company.name;

  switch (aspect) {
    case "stack":
      return `At ${label}, Suraj ${isCurrent ? "works" : "worked"} with: ${company.stack.join(", ")}.`;
    case "duration":
      return isCurrent
        ? `Suraj has been working on ${label} since ${from}${employerNote(company)} — it's his current role.`
        : `Suraj was at ${company.name} from ${from} to ${to}.`;
    case "role":
      return `Suraj's role at ${label}: ${company.role} (${company.period}).`;
    case "work":
      return `Highlights of Suraj's work at ${label}:\n${bullets(company.highlights)}`;
    default:
      return (
        `${label} — ${company.role} (${company.period})\n` +
        (company.employer ? `Employer: ${company.employer}\n` : "") +
        `\n${company.summary}\n\n` +
        `Key work:\n${bullets(company.highlights.slice(0, 3))}`
      );
  }
};

const companySuggestions = (company) => {
  const others = COMPANIES.filter((c) => c.id !== company.id).slice(0, 1);
  return ["Tech stack there?", "What did he work on there?", ...others.map((c) => `Tell me about ${c.name}`)];
};

const projectAnswer = (project, aspect) => {
  const links = [`GitHub: ${project.github}`, project.live && `Live: ${project.live}`].filter(Boolean);
  if (aspect === "stack") return `${project.name} is built with ${project.stack.join(", ")}.`;
  return `${project.name}\n\n${project.description}\n\nStack: ${project.stack.join(", ")}\n${bullets(links)}`;
};

const usedIn = (skillName) => ({
  companies: COMPANIES.filter((c) => c.stack.includes(skillName)).map((c) => c.name),
  projects: PROJECTS.filter((p) => p.stack.includes(skillName)).map((p) => p.name),
});

const skillAnswer = (skills) => {
  const lines = skills.slice(0, 3).map((skill) => {
    const {companies, projects} = usedIn(skill.name);
    const where = [
      companies.length && `at ${companies.join(", ")}`,
      projects.length && `in projects like ${projects.slice(0, 3).join(", ")}`,
    ].filter(Boolean);
    return where.length
      ? `Yes! Suraj has hands-on experience with ${skill.name} — he's used it ${where.join(" and ")}.`
      : `Yes, ${skill.name} is part of Suraj's toolkit.`;
  });
  return lines.join("\n\n");
};

/* ------------------------------------------------------------------
   Public API
   ------------------------------------------------------------------ */

const FALLBACK_TEXT =
  "Hmm, I'm not sure about that one. 🤔 I can tell you about Suraj's experience, skills, projects, education, hobbies or how to contact him. Try one of these:";

export const WELCOME_MESSAGE =
  "Hi! 👋 I'm Suraj's assistant. Ask me about his career, skills, projects, schooling — or pick a question below.";

const rankIntents = (analysis) =>
  INTENTS.map((intent, order) => ({intent, order, score: scoreKeywords(intent.compiled, analysis)}))
    .filter((r) => r.score >= MATCH_THRESHOLD)
    .sort((a, b) => b.score - a.score || a.order - b.order);

/**
 * Work out a reply to `message`.
 * `context` carries what the previous turn was about (for follow-ups like
 * "what stack did they use there?") — pass back the returned `context`.
 *
 * @returns {{text: string, suggestions: string[], context: object}}
 */
export const getBotReply = (message, context = {}) => {
  const analysis = analyze(message);
  const reply = (text, suggestions = DEFAULT_SUGGESTIONS, nextContext = {}) => ({
    text,
    suggestions,
    context: nextContext,
  });

  if (!analysis.raw.length) return reply(FALLBACK_TEXT);

  const top = rankIntents(analysis)[0];
  const aspect = detectAspect(analysis);
  const companies = detectEntities(COMPANY_ENTITIES, analysis);
  const projects = detectEntities(PROJECT_ENTITIES, analysis);
  const skills = detectEntities(SKILL_ENTITIES, analysis);
  const hasEntity = companies.length || projects.length || skills.length;

  if (top && SMALL_TALK.has(top.intent.id) && !hasEntity) {
    return reply(top.intent.answer(), DEFAULT_SUGGESTIONS, context);
  }

  // Follow-ups ("what stack did they use there?") refer to the last entity.
  const isFollowUp = !hasEntity && analysis.hasPronoun && aspect;

  const company =
    companies[0] ||
    (isFollowUp && context.lastCompany && COMPANY_ENTITIES.find((c) => c.id === context.lastCompany));
  if (company) {
    return reply(companyAnswer(company, aspect), companySuggestions(company), {lastCompany: company.id});
  }

  const project =
    projects[0] ||
    (isFollowUp && context.lastProject && PROJECT_ENTITIES.find((p) => p.id === context.lastProject));
  if (project) {
    return reply(projectAnswer(project, aspect), ["What other projects has he built?", "What are his skills?"], {
      lastProject: project.id,
    });
  }

  if (skills.length && (!top || TECH_INTENTS.has(top.intent.id))) {
    return reply(skillAnswer(skills), ["What are all his skills?", "What projects has he built?", "Where does he work now?"]);
  }

  if (top) {
    const {intent} = top;
    return reply(intent.answer(), intent.suggestions || DEFAULT_SUGGESTIONS, intent.context ? intent.context() : {});
  }

  return reply(FALLBACK_TEXT);
};
